/**
 * Persistencia local (spec 2 e 12): a fila de escritas offline.
 *
 * O que mora aqui e o que o servidor AINDA NAO SABE. Nao e um espelho do banco:
 * a leitura offline vem do cache do service worker (network-first com fallback),
 * e manter uma segunda copia das tarefas em IndexedDB criaria dois caches para
 * invalidar e duas versoes da verdade para reconciliar.
 *
 * A fila e IndexedDB, e nao `localStorage`, por tres motivos concretos:
 * `localStorage` e sincrono (trava a thread de UI a cada escrita), tem limite de
 * poucos MB e so guarda string — cada operacao viraria um `JSON.parse` a mais.
 * Alem disso o Background Sync roda no service worker, que nao enxerga
 * `localStorage`.
 */

import Dexie, { type Table } from 'dexie';
import type { SyncAction, SyncEntity } from '@cleartask/shared';

/** Uma escrita esperando rede. */
export interface QueuedOp {
  /** Identidade da OPERACAO, distinta do id da entidade. */
  opId: string;
  entity: SyncEntity;
  action: SyncAction;
  /**
   * Id da entidade alvo. Para `habit_log` e o id do HABITO: um registro diario
   * nao tem id proprio no cliente — sua identidade e (habito, data).
   */
  entityId: string;
  payload: unknown;
  /**
   * Quando a pessoa fez a acao, nao quando a sincronizacao aconteceu. E este
   * carimbo que o servidor compara para detectar edicao concorrente.
   */
  queuedAt: number;
  /**
   * Tentativas ja feitas. Existe para o diagnostico de uma fila que nao escoa —
   * quem decide o descarte e o servidor, devolvendo `rejected`.
   */
  attempts: number;
}

interface MetaRow {
  key: string;
  value: unknown;
}

class ClearTaskDatabase extends Dexie {
  outbox!: Table<QueuedOp, string>;
  meta!: Table<MetaRow, string>;

  constructor() {
    super('cleartask');

    // `queuedAt` indexado: a fila e sempre drenada em ordem de intencao.
    this.version(1).stores({
      outbox: 'opId, queuedAt, entityId',
      meta: 'key',
    });
  }
}

export const db = new ClearTaskDatabase();

const CHAVE_SINCE = 'syncSince';

/**
 * Ancora do proximo delta: o `now` do SERVIDOR na ultima sincronizacao.
 *
 * Guardar o relogio do dispositivo aqui seria o bug classico do delta sync — um
 * aparelho adiantado em 3 minutos pediria "o que mudou desde o futuro" e
 * perderia em silencio tudo que aconteceu nesses 3 minutos.
 */
export async function readSyncSince(): Promise<number> {
  const row = await db.meta.get(CHAVE_SINCE);
  return typeof row?.value === 'number' ? row.value : 0;
}

export async function writeSyncSince(serverNow: number): Promise<void> {
  await db.meta.put({ key: CHAVE_SINCE, value: serverNow });
}

/** Operacoes pendentes, na ordem em que a pessoa as fez. */
export function pendingOps(): Promise<QueuedOp[]> {
  return db.outbox.orderBy('queuedAt').toArray();
}

export function countPendingOps(): Promise<number> {
  return db.outbox.count();
}

export async function enqueueOp(op: QueuedOp): Promise<void> {
  await db.outbox.put(op);
}

export async function removeOps(opIds: string[]): Promise<void> {
  if (opIds.length === 0) return;
  await db.outbox.bulkDelete(opIds);
}

/**
 * Recarimba a operacao com o relogio de agora.
 *
 * E assim que "manter a minha versao" e resolvido, e a razao esta na regra do
 * servidor: `estaDefasada = base.updatedAt > op.queuedAt` (apps/api/src/routes/
 * sync.ts). A operacao voltou como conflito porque foi enfileirada ANTES da
 * escrita que ja estava la; com o carimbo de agora ela deixa de estar defasada
 * e a proxima drenagem a aplica.
 *
 * A alternativa seria um campo `force` no protocolo de sync. Recarimbar diz a
 * mesma coisa com o vocabulario que a API ja tem — e mantem o servidor sem uma
 * porta que aceita sobrescrever sem comparar nada, que e o tipo de porta que
 * um dia alguem usa por engano.
 *
 * O que se perde: a ordem da fila muda, porque `pendingOps` ordena por
 * `queuedAt`. E o desejado — a decisao de manter a minha versao acabou de ser
 * tomada, e portanto e a mais recente que existe sobre aquela linha.
 */
export async function restampOp(opId: string, queuedAt: number): Promise<void> {
  const atual = await db.outbox.get(opId);
  if (!atual) return;
  await db.outbox.put({ ...atual, queuedAt, attempts: 0 });
}

export async function bumpAttempts(opIds: string[]): Promise<void> {
  await db.transaction('rw', db.outbox, async () => {
    for (const opId of opIds) {
      const atual = await db.outbox.get(opId);
      if (atual) await db.outbox.put({ ...atual, attempts: atual.attempts + 1 });
    }
  });
}
